/* ===========================
IMPORTS
=========================== */
import React, { Component } from 'react';
import {
  Route,
  withRouter,
  Switch,
  Redirect,
} from 'react-router-dom';
import PropTypes from 'prop-types';
// IMPORT - AWS
import {
  Amplify, Auth, Hub, I18n,
} from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
// IMPORT - MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
/* eslint-disable-next-line import/no-unresolved, import/extensions */
import awsExports from '../aws-exports';

// IMPORT - STYLING
import '../assets/css/App.css';
import '../assets/css/Buttons.css';
import '../assets/css/Fonts.css';
import '../assets/css/Forms.css';
import '../assets/css/Canvas.css';
/* eslint-disable-next-line import/no-unresolved */
import '@aws-amplify/ui-react/styles.css';

// IMPORT - MFA
import {
  getMfaStatus, requestMfaChallenge, invalidateMfaSession, useBackupCode,
} from '../MfaApi';
import MfaChallengeModal from './MfaChallengeModal';
import MfaSetupWizard from './MfaSetupWizard';

// IMPORT - COMPONENTS
import Navigation from './Elements/Navigation';
import CanvasList from './Canvases/List';
import CanvasView from './Canvases/View';
import CanvasUpload from './Canvases/Upload';
import BundlesList from './Bundles/List';
import BundlesView from './Bundles/View';
import ApksList from './Apks/List';
import FirmwaresList from './Firmwares/List';
import HistoriesList from './Histories/List';
import RulesList from './Rules/List';

// IMPORT - ELEMENTS
import Loader from './Elements/Loader';
import Footer from './Elements/Footer';

I18n.putVocabularies({
  en: {
    'Password attempts exceeded': "You've exceeded the maximum number of allowed login attempts. For security reasons, we have temporarily restricted access to your account. Please try again later or reset your password.",
  },
});

/* ===========================
PRIVATE ROUTES HANDLING
=========================== */
// eslint-disable-next-line react/prop-types
function PrivateRoute({ component: Component, ...rest }) {
  return (
    <Route
      {...rest}
      render={(props) => <Component {...props} {...rest} />}
    />
  );
}

/* ===========================
LOCKED SCREEN
=========================== */
// eslint-disable-next-line react/prop-types
function LockedScreen({ mfaToken, onUnlocked, onSignOut }) {
  const [code, setCode] = React.useState('');
  const [backupError, setBackupError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const handleBackupSubmit = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setBackupError(null);
    try {
      await useBackupCode(mfaToken, trimmed);
      onUnlocked();
    } catch {
      setBackupError('Invalid backup code. Please try again.');
      setSubmitting(false);
    }
  };

  const boxSx = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    p: 3,
  };
  const btnSx = { bgcolor: '#01070B', '&:hover': { bgcolor: '#2c2c2c' } };
  return (
    <Box sx={boxSx}>
      <Typography variant="h5" fontWeight={700} mb={2}>Account temporarily locked</Typography>
      <Typography variant="body1" color="text.secondary" mb={4} textAlign="center" maxWidth={400}>
        Too many failed attempts. Enter a backup code to regain access, or wait 30 minutes.
      </Typography>
      <Box sx={{ width: '100%', maxWidth: 360, mb: 3 }}>
        <input
          value={code}
          onChange={(e) => { setCode(e.target.value); setBackupError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleBackupSubmit(); }}
          placeholder="Backup code"
          data-testid="backup-code-input"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: '14px',
            border: backupError ? '1px solid #d32f2f' : '1px solid #ccc',
            borderRadius: '4px',
            boxSizing: 'border-box',
          }}
        />
        {backupError && (
          <Typography variant="caption" color="error" display="block" mt={0.5}>
            {backupError}
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={handleBackupSubmit}
          disabled={submitting || !code.trim()}
          fullWidth
          data-testid="backup-code-submit-btn"
          sx={{ mt: 1, ...btnSx }}
        >
          {submitting ? 'Verifying…' : 'Unlock with backup code'}
        </Button>
      </Box>
      <Button variant="outlined" onClick={onSignOut} data-testid="account-locked-return-btn">
        Return to Login
      </Button>
    </Box>
  );
}

/* ===========================
CLASS
=========================== */
class App extends Component {
  /* ===========================
  DEFAULT
  =========================== */
  /**
  * Main constructor of Component
  */
  constructor(props) {
    super(props);
    this.state = {
      loadingApp: true,
      loadingContent: true,
      mfaGate: 'loading',
      mfaMethod: null,
      mfaEnrolledMethods: [],
      mfaToken: null,
    };

    this.updateLoadingApp = this.updateLoadingApp.bind(this);
    this.updateLoadingContent = this.updateLoadingContent.bind(this);
    this.handleAuthSuccess = this.handleAuthSuccess.bind(this);
    this.doSignOut = this.doSignOut.bind(this);

    Amplify.configure(awsExports);
  }

  /* ===========================
  LIFECYCLE
  =========================== */
  componentDidMount() {
    this.hubListener = Hub.listen('auth', ({ payload: { event, data } }) => {
      if (event === 'signIn') {
        const { mfaGate } = this.state;
        if (mfaGate !== 'challenge' && mfaGate !== 'setup' && mfaGate !== 'locked') {
          this.handleAuthSuccess(data);
        }
      }
      if (event === 'signOut') this.setState({ mfaGate: 'loading', mfaToken: null });
    });
    Auth.currentAuthenticatedUser()
      .then((user) => this.handleAuthSuccess(user))
      .catch(() => {});
  }

  componentWillUnmount() {
    if (typeof this.hubListener === 'function') this.hubListener();
  }

  /* ===========================
  FUNCTIONS
  =========================== */
  /**
  * Updates app loading status
  */
  updateLoadingApp(loading) {
    this.setState({
      loadingApp: loading,
    });
  }

  /**
  * Updates content loading status
  */
  updateLoadingContent(loading) {
    this.setState({
      loadingContent: loading,
    });
  }

  async handleAuthSuccess(user) {
    const token = user?.signInUserSession?.idToken?.jwtToken;
    if (!token) {
      this.setState({ mfaGate: 'verified' });
      return;
    }

    localStorage.setItem('accessToken', token);
    this.setState({ mfaToken: token });

    try {
      const status = await getMfaStatus(token);
      if (status.isLocked) {
        this.setState({ mfaGate: 'locked' });
      } else if (status.hasActiveSession) {
        this.setState({ mfaGate: 'verified' });
      } else if (!status.isEnrolled) {
        this.setState({ mfaGate: 'setup' });
      } else {
        if (status.method !== 'totp') {
          await requestMfaChallenge(token).catch(() => {});
        }
        this.setState({ mfaGate: 'challenge', mfaMethod: status.method, mfaEnrolledMethods: status.enrolledMethods || [] });
      }
    } catch (err) {
      if (err?.status !== 404) {
        // Non-404 error: sign out rather than silently bypassing MFA.
        await Auth.signOut().catch(() => {});
        this.setState({ mfaGate: 'loading', mfaToken: null });
        return;
      }
      // 404 = MFA not enforced for this app — proceed normally.
      this.setState({ mfaGate: 'verified' });
    }
  }

  doSignOut(amplifySignOut) {
    const { mfaToken } = this.state;
    if (mfaToken) invalidateMfaSession(mfaToken).catch(() => {});
    this.setState({ mfaGate: 'loading', mfaToken: null });
    amplifySignOut();
  }

  /* ===========================
  RENDER
  =========================== */
  app(signOut, user) {
    return (
      <div className={`App ${((this.state.loadingApp) ? 'loading-app ' : '')} ${((this.state.loadingContent) ? 'loading-content ' : '')}`}>
        <Loader classes="loader-application" />

        {/* Show navigation */}
        <Route path="/dashboard/*" render={() => (<Navigation signOut={signOut} updateLoadingApp={this.updateLoadingApp} user={user} />)} />

        <div id="content" className={`${(window.location.href.indexOf('dashboard/rules') > -1) ? 'Section-Rules' : ''}`}>
          <Loader classes="loader-content" />

          <main>
            <Switch>
              <PrivateRoute exact path="/dashboard/canvases" user={user} updateLoadingApp={this.updateLoadingApp} updateLoadingContent={this.updateLoadingContent} component={CanvasList} />
              <PrivateRoute exact path="/dashboard/canvases/upload" user={user} updateLoadingApp={this.updateLoadingApp} updateLoadingContent={this.updateLoadingContent} component={CanvasUpload} />
              <PrivateRoute exact path="/dashboard/canvases/:id/:section" user={user} updateLoadingApp={this.updateLoadingApp} updateLoadingContent={this.updateLoadingContent} component={CanvasView} />
              <Route exact path="/dashboard/canvases/:id" render={(props) => (<Redirect to={`/dashboard/canvases/${props.match.params.id}/details`} />)} />

              <PrivateRoute exact path="/dashboard/bundles" user={user} updateLoadingApp={this.updateLoadingApp} updateLoadingContent={this.updateLoadingContent} component={BundlesList} />
              <PrivateRoute exact path="/dashboard/bundles/:id/:section" user={user} updateLoadingApp={this.updateLoadingApp} updateLoadingContent={this.updateLoadingContent} component={BundlesView} />
              <Route exact path="/dashboard/bundles/:id" render={(props) => (<Redirect to={`/dashboard/bundles/${props.match.params.id}/details`} />)} />

              <PrivateRoute exact path="/dashboard/histories" user={user} updateLoadingApp={this.updateLoadingApp} updateLoadingContent={this.updateLoadingContent} component={HistoriesList} />

              <PrivateRoute exact path="/dashboard/apks" user={user} updateLoadingApp={this.updateLoadingApp} updateLoadingContent={this.updateLoadingContent} component={ApksList} />
              <PrivateRoute exact path="/dashboard/firmwares" user={user} updateLoadingApp={this.updateLoadingApp} updateLoadingContent={this.updateLoadingContent} component={FirmwaresList} />

              <PrivateRoute exact path="/dashboard/rules" user={user} updateLoadingApp={this.updateLoadingApp} updateLoadingContent={this.updateLoadingContent} component={RulesList} />
            </Switch>
          </main>

          {/* Handle Default Path */}
          <Route
            exact
            path="/dashboard"
            render={() => <Redirect to="/dashboard/canvases" />}
          />

          {/* Handle Default Path */}
          <Route
            exact
            path="/"
            render={() => <Redirect to="/dashboard/canvases" />}
          />
        </div>

        <Footer />
      </div>
    );
  }

  render() {
    const {
      mfaGate, mfaMethod, mfaEnrolledMethods, mfaToken,
    } = this.state;
    return (
      <Authenticator hideSignUp>
        {({ signOut, user }) => {
          Auth.currentAuthenticatedUser()
            .then(({ signInUserSession }) => {
              const { jwtToken } = signInUserSession.idToken ?? '';

              if (jwtToken && localStorage.accessToken !== jwtToken) {
                localStorage.setItem('accessToken', jwtToken);
              }

              if (window.location.pathname === '/login') {
                this.props.history.push('/dashboard');
                window.location.pathname = '/';
              }
            })
            .catch(() => {
              if (window.location.pathname !== '/login') {
                this.props.history.push('/login');
                window.location.pathname = '/login';
              }
            });

          if (mfaGate === 'loading') {
            return <div className="App"><Loader classes="loader-application" /></div>;
          }

          if (mfaGate === 'challenge') {
            return (
              <MfaChallengeModal
                token={mfaToken}
                method={mfaMethod}
                enrolledMethods={mfaEnrolledMethods}
                onVerified={() => this.setState({ mfaGate: 'verified' })}
                onNotEnrolled={() => this.setState({ mfaGate: 'setup' })}
                onLockedOut={() => this.setState({ mfaGate: 'locked' })}
                onSignOut={() => this.doSignOut(signOut)}
              />
            );
          }

          if (mfaGate === 'setup') {
            return (
              <MfaSetupWizard
                token={mfaToken}
                onEnrolled={() => this.setState({ mfaGate: 'verified' })}
                onCancel={() => this.doSignOut(signOut)}
              />
            );
          }

          if (mfaGate === 'locked') {
            return (
              <LockedScreen
                mfaToken={mfaToken}
                onUnlocked={() => this.setState({ mfaGate: 'verified' })}
                onSignOut={() => this.doSignOut(signOut)}
              />
            );
          }

          return (
            this.app(signOut, user)
          );
        }}
      </Authenticator>
    );
  }
}

/* ===========================
PROPERTY TYPES
=========================== */
App.propTypes = {
  history: PropTypes.object,
};

/* ===========================
EXPORT
=========================== */
export default withRouter(App);
